// Pure, React-free helpers for the "Pacing vs forecast" panel on the forecast page.
//
// Goal: compare the CURRENT in-progress period's actual QuickBooks data against the
// forecast for that same period — month-to-date (monthly grain) or week-to-date
// (weekly/daily grain). Pace-to-date only: actual-so-far vs the forecast prorated to
// today. No run-rate projection (lumpy / one-time items make extrapolation misleading).
//
// All date math is UTC-based via forecastBuildState helpers to avoid month-boundary drift.

import type { QuickBooksFpaStatementRender } from '../../../data/ServerApiClient'
import {
  addDaysIso,
  daysBetweenIso,
  daysInMonthIso,
  monthOffsetIso,
  monthStartIso,
  weekStartIso,
} from './forecastBuildState'

export type PacingStatus = 'ahead' | 'on_track' | 'behind' | 'unknown'
// 'flow' lines accumulate over the period (revenue, expenses, net cash change) and are
// prorated; 'point_in_time' lines (ending cash) are a balance and are NOT prorated.
export type PacingLineKind = 'flow' | 'point_in_time'
export type PacingFavorable = 'higher' | 'lower'
export type PacingGrain = 'month' | 'week'

export type PacingMatcher = { nodeId?: string; label?: RegExp }

export type PacingLineInput = {
  id: string
  label: string
  kind: PacingLineKind
  favorable: PacingFavorable
  forecastFull: number | null // forecast value for the whole current period
  actualToDate: number | null // actuals realized so far (null = nothing synced yet)
}

export type PacingLineResult = PacingLineInput & {
  expectedToDate: number | null // flow: forecastFull * elapsedFraction; point_in_time: forecastFull
  varianceToDate: number | null // actualToDate - expectedToDate
  pacePct: number | null // actualToDate / expectedToDate
  status: PacingStatus
}

export type PacingPeriod = {
  kind: PacingGrain
  periodStartIso: string
  daysElapsed: number
  daysInPeriod: number
  elapsedFraction: number
  isPeriodComplete: boolean
}

export type PacingModel = {
  available: boolean
  grain: PacingGrain
  period: PacingPeriod | null
  lines: PacingLineResult[]
  asOfActualsLabel: string
  warning: string | null
}

// ~2.5 days of a 31-day month — keeps an early-period prorated forecast from collapsing to ~0.
export const MIN_ELAPSED_FRACTION = 0.08
// Generous band: lumpy / one-time postings (rent on day 1, etc.) make a tight band noisy.
export const PACE_TOLERANCE = 0.1

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value))

const isIsoDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value)

// First period flagged 'forecast' in a merged actual+forecast statement. Because the
// forecast is anchored to the most recent closed period, this is the current in-progress
// period. Returns null when the statement is all-actual (period already closed).
export function firstForecastPeriod(
  periodKinds: string[] | undefined | null,
  periods: Array<string | null> | undefined | null,
): { index: number; periodIso: string } | null {
  if (!periodKinds?.length || !periods?.length) return null
  for (let index = 0; index < periodKinds.length; index += 1) {
    if (periodKinds[index] === 'forecast') {
      const periodIso = String(periods[index] || '').slice(0, 10)
      return periodIso ? { index, periodIso } : null
    }
  }
  return null
}

const findRow = (
  rows: QuickBooksFpaStatementRender['rows'],
  matchers: PacingMatcher[],
) => {
  for (const matcher of matchers) {
    const found = rows.find((row) => {
      if (matcher.nodeId && row.node_id === matcher.nodeId) return true
      if (matcher.label && matcher.label.test(String(row.label || ''))) return true
      return false
    })
    if (found) return found
  }
  return null
}

// Read a single line's value for the column whose period falls in `monthIso`. Matchers are
// tried in order (put node-id matchers before label fallbacks so node id wins).
export function statementValueForMonth(
  statement: QuickBooksFpaStatementRender | null | undefined,
  matchers: PacingMatcher[],
  monthIso: string,
): number | null {
  if (!statement?.rows?.length || !statement.periods?.length) return null
  const periodIndex = statement.periods.findIndex(
    (period) => monthStartIso(String(period || '').slice(0, 10)) === monthIso,
  )
  if (periodIndex < 0) return null
  const row = findRow(statement.rows, matchers)
  if (!row) return null
  const raw = Number(row.values?.[periodIndex])
  return Number.isFinite(raw) ? raw : null
}

// Build the period frame (how far we are through the current month/week).
export function buildPacingPeriod(
  kind: PacingGrain,
  periodStartRaw: string,
  todayIso: string,
): PacingPeriod | null {
  const periodStartIso = kind === 'month' ? monthStartIso(periodStartRaw) : weekStartIso(periodStartRaw)
  if (!isIsoDay(periodStartIso) || !isIsoDay(todayIso)) return null
  const daysInPeriod = kind === 'month' ? daysInMonthIso(periodStartIso) : 7
  const endExclusive = kind === 'month'
    ? monthOffsetIso(periodStartIso, 1) || addDaysIso(periodStartIso, daysInPeriod)
    : addDaysIso(periodStartIso, 7)
  const isPeriodComplete = todayIso >= endExclusive
  const rawElapsed = daysBetweenIso(periodStartIso, todayIso) + 1
  const daysElapsed = isPeriodComplete ? daysInPeriod : clamp(rawElapsed, 1, daysInPeriod)
  const elapsedFraction = isPeriodComplete
    ? 1
    : clamp(daysElapsed / daysInPeriod, MIN_ELAPSED_FRACTION, 1)
  return { kind, periodStartIso, daysElapsed, daysInPeriod, elapsedFraction, isPeriodComplete }
}

export function computePacingLine(input: PacingLineInput, period: PacingPeriod): PacingLineResult {
  const { forecastFull, actualToDate, kind, favorable } = input
  const expectedToDate = forecastFull == null
    ? null
    : kind === 'point_in_time'
      ? forecastFull
      : forecastFull * period.elapsedFraction
  const varianceToDate = actualToDate != null && expectedToDate != null
    ? actualToDate - expectedToDate
    : null
  const pacePct = actualToDate != null && expectedToDate != null && expectedToDate !== 0
    ? actualToDate / expectedToDate
    : null

  let status: PacingStatus = 'unknown'
  if (actualToDate != null && expectedToDate != null && expectedToDate !== 0) {
    let delta = (actualToDate - expectedToDate) / Math.abs(expectedToDate)
    if (favorable === 'lower') delta = -delta
    status = Math.abs(delta) <= PACE_TOLERANCE ? 'on_track' : delta > PACE_TOLERANCE ? 'ahead' : 'behind'
  }

  return { ...input, expectedToDate, varianceToDate, pacePct, status }
}

export function buildPacingModel(args: {
  grain: PacingGrain
  period: PacingPeriod | null
  lines: PacingLineInput[]
  asOfActualsLabel: string
  warning?: string | null
}): PacingModel {
  const { grain, period, lines, asOfActualsLabel } = args
  if (!period) {
    return { available: false, grain, period: null, lines: [], asOfActualsLabel, warning: args.warning ?? null }
  }
  const results = lines.map((line) => computePacingLine(line, period))
  const hasForecast = results.some((line) => line.forecastFull != null)
  const hasActual = results.some((line) => line.actualToDate != null)
  let warning = args.warning ?? null
  if (hasForecast && !hasActual) warning = warning || 'No actuals synced yet for this period.'
  return {
    available: hasForecast || hasActual,
    grain,
    period,
    lines: results,
    asOfActualsLabel,
    warning,
  }
}
